import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect } from 'react';

type BillboardListPaginationProps = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
};

export function BillboardListPagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}: BillboardListPaginationProps) {
  if (totalPages <= 1 && totalItems <= itemsPerPage) return null;

  const safeStart =
    totalItems === 0
      ? 0
      : Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
  const safeEnd = Math.max(
    safeStart,
    Math.min(currentPage * itemsPerPage, totalItems),
  );



  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [currentPage]);

  return (
    <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-[#ECEAF5] pt-6 sm:flex-row">
      <p className="text-sm font-medium text-[#6A6688]">
        {totalItems === 0
          ? 'Showing 0 screens'
          : safeStart === safeEnd
            ? `Showing ${safeEnd} of ${totalItems} screens`
            : `Showing ${safeStart}–${safeEnd} of ${totalItems} screens`}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="inline-flex h-10 cursor-pointer items-center gap-1 rounded-[14px] border border-[#D9D3F0] bg-white px-4 text-sm font-bold text-[#2B3E8A] transition hover:bg-[#F4F2FB] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft size={16} />
          Prev
        </button>
        <span className="min-w-[4.5rem] text-center text-sm font-extrabold text-[#3A3660]">
          {Math.min(currentPage, totalPages)} / {totalPages}
        </span>
        <button
          type="button"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="inline-flex h-10 cursor-pointer items-center gap-1 rounded-[14px] border border-[#D9D3F0] bg-white px-4 text-sm font-bold text-[#2B3E8A] transition hover:bg-[#F4F2FB] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
